/**
 * Wholesale Collection JavaScript
 * Handles:
 * - Grid/List view toggle with localStorage persistence
 * - Quantity input +/- buttons with minimum order enforcement
 * - Quick Order running total calculation
 * - Bulk add to cart integration with Liquid Ajax Cart
 * - Facets.js integration for AJAX filter updates
 */

(function() {
  'use strict';

  // Search state
  var currentCollectionHandle = null;
  var isSearchActive = false;

  // Storefront API state
  var storefrontApiToken = null;
  var shopDomain = null;
  var cachedCollectionProducts = null;
  var isLoadingProducts = false;
  var productsLoadPromise = null;

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initViewToggle();
    initQuantityControls();
    initQuickOrder();
    initFormSubmitHandlers();
    initDescriptionToggle();
    initProductSearch();
    initBulkAddButton();
  }

  /**
   * Grid/List View Toggle
   * Persists preference to localStorage
   */
  function initViewToggle() {
    const viewButtons = document.querySelectorAll('.wholesale-grid__view-btn');
    // Target main product grid specifically by ID, not the search results grid
    const productGrid = document.getElementById('product-grid');

    if (!viewButtons.length || !productGrid) return;

    // Load saved preference
    const savedView = localStorage.getItem('wholesale-view') || 'grid';
    setView(savedView, false);

    // Remove early-load class now that JS has taken over
    document.documentElement.classList.remove('wholesale-view-list-pending');

    viewButtons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        const view = this.dataset.view;
        setView(view, true);
      });
    });

    function setView(view, save) {
      // Update button states
      viewButtons.forEach(function(btn) {
        btn.classList.toggle('active', btn.dataset.view === view);
      });

      // Query fresh DOM element each time (facets.js replaces the grid on sort/filter)
      var currentGrid = document.getElementById('product-grid');
      if (currentGrid) {
        currentGrid.classList.remove('view-grid', 'view-list');
        currentGrid.classList.add('view-' + view);
      }

      // Also update search results grid if it exists
      var searchResultsGrid = document.getElementById('search-results-grid');
      if (searchResultsGrid) {
        searchResultsGrid.classList.remove('view-grid', 'view-list');
        searchResultsGrid.classList.add('view-' + view);
      }

      // Save preference
      if (save) {
        localStorage.setItem('wholesale-view', view);
      }
    }
  }

  /**
   * Quantity Control Buttons (+/-)
   * Handles increment/decrement with minimum order qty enforcement
   */
  function initQuantityControls() {
    // Use event delegation for dynamically added elements
    document.addEventListener('click', function(e) {
      if (e.target.closest('.wholesale-card__qty-btn--minus')) {
        handleQuantityChange(e.target.closest('.wholesale-card__qty-btn--minus'), -1);
      } else if (e.target.closest('.wholesale-card__qty-btn--plus')) {
        handleQuantityChange(e.target.closest('.wholesale-card__qty-btn--plus'), 1);
      }
    });

    // Direct input change
    document.addEventListener('change', function(e) {
      if (e.target.matches('.wholesale-card__qty-input')) {
        validateQuantityInput(e.target);
        updateQuickOrderTotal();
      }
    });

    // Input focus - select all text for easy replacement
    document.addEventListener('focus', function(e) {
      if (e.target.matches('.wholesale-card__qty-input')) {
        e.target.select();
      }
    }, true);
  }

  function handleQuantityChange(button, delta) {
    var container = button.closest('.wholesale-card__quantity');
    if (!container) return;

    var input = container.querySelector('.wholesale-card__qty-input');
    if (!input) return;

    var minQty = parseInt(input.dataset.minQty, 10) || 1;
    var currentValue = parseInt(input.value, 10) || minQty;

    var newValue = currentValue + delta;

    // Allow 0 to deselect from quick order, otherwise enforce minimum
    if (newValue < 0) {
      newValue = 0;
    } else if (newValue > 0 && newValue < minQty) {
      // If going from 0 upward, jump to minimum
      if (delta > 0) {
        newValue = minQty;
      } else {
        newValue = 0;
      }
    }

    input.value = newValue;
    updateQuickOrderTotal();
  }

  function validateQuantityInput(input) {
    var minQty = parseInt(input.dataset.minQty, 10) || 1;
    var value = parseInt(input.value, 10);

    // Handle NaN
    if (isNaN(value) || value < 0) {
      value = 0;
    }

    // If between 0 and min, round to nearest valid (0 or min)
    if (value > 0 && value < minQty) {
      value = minQty;
    }

    input.value = value;
  }

  /**
   * Quick Order Functionality
   * Tracks selected items and calculates running total
   */
  function initQuickOrder() {
    var quickOrderBar = document.getElementById('wholesale-quick-order');
    if (!quickOrderBar) return;

    // Initial calculation
    updateQuickOrderTotal();

    // Submit button handler
    var submitBtn = quickOrderBar.querySelector('[data-quick-order-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', handleBulkAdd);
    }
  }

  function updateQuickOrderTotal() {
    var quickOrderBar = document.getElementById('wholesale-quick-order');
    if (!quickOrderBar) return;

    var countEl = quickOrderBar.querySelector('[data-quick-order-count]');
    var totalEl = quickOrderBar.querySelector('[data-quick-order-total]');
    var submitBtn = quickOrderBar.querySelector('[data-quick-order-submit]');

    var totalItems = 0;
    var totalPrice = 0;
    var productCount = 0;

    document.querySelectorAll('.wholesale-card__qty-input').forEach(function(input) {
      var qty = parseInt(input.value, 10) || 0;
      var minQty = parseInt(input.dataset.minQty, 10) || 1;

      if (qty >= minQty) {
        var pricePerUnit = parseInt(input.dataset.pricePerUnit, 10) || 0;
        totalItems += qty;
        totalPrice += qty * pricePerUnit;
        productCount++;
      }
    });

    // Update display
    if (countEl) {
      countEl.textContent = totalItems;
    }

    if (totalEl) {
      totalEl.textContent = formatMoney(totalPrice);
    }

    // Show/hide bar and enable/disable button
    if (totalItems > 0) {
      quickOrderBar.classList.add('visible');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Add ' + productCount + ' ' + (productCount === 1 ? 'Product' : 'Products') + ' to Cart';
      }
    } else {
      quickOrderBar.classList.remove('visible');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Add All to Cart';
      }
    }
  }

  /**
   * Format money in GBP
   * Uses Shopify's money_format if available
   */
  function formatMoney(cents) {
    // Try Shopify's format function
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    // Fallback to simple GBP format
    var amount = (cents / 100).toFixed(2);
    return '£' + amount;
  }

  /**
   * Individual Add to Cart Button Handlers
   * Uses AJAX to add to cart without form submission
   */
  function initFormSubmitHandlers() {
    document.addEventListener('click', function(e) {
      var button = e.target.closest('.wholesale-card__add-btn');
      if (!button || button.disabled) return;

      var form = button.closest('.wholesale-card__form');
      if (!form) return;

      var variantId = form.querySelector('input[name="id"]').value;
      var quantityInput = form.querySelector('input[name="quantity"]');
      var quantity = parseInt(quantityInput.value, 10) || 1;

      // Show loading state
      button.classList.add('loading');
      button.disabled = true;

      var item = {
        id: parseInt(variantId, 10),
        quantity: quantity
      };

      // Try Liquid Ajax Cart first, then fallback to fetch
      if (window.liquidAjaxCart && typeof window.liquidAjaxCart.cartRequestAdd === 'function') {
        window.liquidAjaxCart.cartRequestAdd({ items: [item] }, {
          lastComplete: function(requestState) {
            if (requestState.responseData && requestState.responseData.ok) {
              handleIndividualAddSuccess(button);
            } else {
              handleIndividualAddError(button, requestState.fetchError || 'Add to cart failed');
            }
          }
        });
      } else {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: [item] })
        })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Add to cart failed');
          }
          return response.json();
        })
        .then(function() {
          handleIndividualAddSuccess(button);

          // Refresh cart state if Liquid Ajax Cart is available
          if (window.liquidAjaxCart) {
            window.liquidAjaxCart.get();
          }
        })
        .catch(function(error) {
          handleIndividualAddError(button, error);
        });
      }
    });
  }

  function handleIndividualAddSuccess(button) {
    button.classList.remove('loading');
    button.disabled = false;

    // Show success state
    var originalText = button.textContent.trim();
    button.innerHTML = 'Added! <span class="wholesale-card__add-spinner" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';

    setTimeout(function() {
      button.innerHTML = originalText + ' <span class="wholesale-card__add-spinner" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';
    }, 2000);
  }

  function handleIndividualAddError(button, error) {
    console.error('Add to cart error:', error);
    button.classList.remove('loading');
    button.disabled = false;

    var originalText = button.textContent.trim();
    button.innerHTML = 'Error <span class="wholesale-card__add-spinner" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';

    setTimeout(function() {
      button.innerHTML = originalText + ' <span class="wholesale-card__add-spinner" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>';
    }, 3000);
  }

  /**
   * Bulk Add All
   * Collects all selected items and adds them to cart
   * Integrates with Liquid Ajax Cart if available
   */
  function handleBulkAdd(e) {
    e.preventDefault();

    var button = e.target;
    var quickOrderBar = document.getElementById('wholesale-quick-order');

    // Collect items to add
    var items = [];

    document.querySelectorAll('.wholesale-card').forEach(function(card) {
      var input = card.querySelector('.wholesale-card__qty-input');
      var variantId = card.dataset.variantId;
      var qty = parseInt(input.value, 10) || 0;
      var minQty = parseInt(input.dataset.minQty, 10) || 1;

      if (qty >= minQty && variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: qty
        });
      }
    });

    if (items.length === 0) return;

    // Disable button during request
    button.disabled = true;
    var originalText = button.textContent;
    button.textContent = 'Adding...';

    // Try Liquid Ajax Cart first, then fallback to fetch
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.cartRequestAdd === 'function') {
      // Use Liquid Ajax Cart's cartRequestAdd method
      window.liquidAjaxCart.cartRequestAdd({ items: items }, {
        lastComplete: function(requestState) {
          if (requestState.responseData && requestState.responseData.ok) {
            handleBulkAddSuccess(button, originalText);
          } else {
            handleBulkAddError(button, originalText, requestState.fetchError || 'Add to cart failed');
          }
        }
      });
    } else {
      // Fallback to fetch API
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: items })
      })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      })
      .then(function() {
        handleBulkAddSuccess(button, originalText);

        // Refresh cart state (trigger cart drawer update if available)
        if (window.liquidAjaxCart && window.liquidAjaxCart.cartRequestGet) {
          window.liquidAjaxCart.cartRequestGet();
        } else {
          // Force page refresh to update cart
          window.location.reload();
        }
      })
      .catch(function(error) {
        handleBulkAddError(button, originalText, error);
      });
    }
  }

  function handleBulkAddSuccess(button, originalText) {
    // Reset all quantities to their minimums
    document.querySelectorAll('.wholesale-card__qty-input').forEach(function(input) {
      var minQty = parseInt(input.dataset.minQty, 10) || 1;
      input.value = minQty;
    });

    // Update quick order display
    updateQuickOrderTotal();

    // Show success message
    button.textContent = 'Added!';
    button.disabled = false;

    setTimeout(function() {
      button.textContent = originalText;
    }, 2000);
  }

  function handleBulkAddError(button, originalText, error) {
    console.error('Bulk add error:', error);
    button.textContent = 'Error - Try Again';
    button.disabled = false;

    setTimeout(function() {
      button.textContent = originalText;
    }, 3000);
  }

  /**
   * Product Search
   * Uses Storefront API for full collection search (if token available)
   * Falls back to Predictive Search API (limited to 10 results)
   */
  function initProductSearch() {
    var searchInput = document.getElementById('wholesale-search');
    var productGridContainer = document.getElementById('ProductGridContainer');
    var clearBtn = document.querySelector('.wholesale-grid__search-clear');

    if (!searchInput) return;

    // Get collection handle, shop domain, and Storefront API token from data attributes
    if (productGridContainer) {
      currentCollectionHandle = productGridContainer.dataset.collectionHandle;
      shopDomain = productGridContainer.dataset.shopDomain;
      storefrontApiToken = productGridContainer.dataset.storefrontToken;
    }

    // If we have a Storefront API token, start loading all collection products in background
    if (storefrontApiToken && shopDomain && currentCollectionHandle) {
      loadAllCollectionProducts();
    }

    var debounceTimer;

    searchInput.addEventListener('input', function() {
      clearTimeout(debounceTimer);
      var searchTerm = searchInput.value.trim();

      debounceTimer = setTimeout(function() {
        if (searchTerm.length >= 2) {
          searchCollection(searchTerm);
        } else if (searchTerm.length === 0) {
          clearSearch();
        }
      }, 300);
    });

    // Clear search on escape key
    searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        searchInput.value = '';
        clearSearch();
      }
    });

    // Clear button handler
    if (clearBtn) {
      clearBtn.addEventListener('click', function() {
        searchInput.value = '';
        clearSearch();
        searchInput.focus();
      });
    }
  }

  /**
   * Search collection using Storefront API (full collection) or Predictive Search API (fallback)
   * If Storefront API token is configured, searches all products in the collection
   * Otherwise falls back to Predictive Search API (limited to 10 results)
   */
  function searchCollection(searchTerm) {
    var searchResultsContainer = document.getElementById('wholesale-search-results');
    var searchResultsGrid = document.getElementById('search-results-grid');
    var searchCountEl = document.querySelector('.wholesale-grid__search-count');
    var productGrid = document.getElementById('product-grid');
    var pagination = document.querySelector('.pagination');

    if (!searchResultsContainer || !searchResultsGrid) return;

    // Show loading state
    searchResultsContainer.style.display = 'block';
    searchResultsGrid.innerHTML = '<li class="wholesale-grid__search-loading">Searching...</li>';
    if (productGrid) productGrid.style.display = 'none';
    if (pagination) pagination.style.display = 'none';

    isSearchActive = true;

    // If we have cached products from Storefront API, use client-side search
    if (cachedCollectionProducts) {
      var matchingProducts = searchCachedProducts(searchTerm);
      renderSearchResults(matchingProducts, searchTerm, searchCountEl, searchResultsGrid);
      return;
    }

    // If products are still loading, wait for them
    if (isLoadingProducts && productsLoadPromise) {
      productsLoadPromise.then(function(products) {
        if (products) {
          var matchingProducts = searchCachedProducts(searchTerm);
          renderSearchResults(matchingProducts, searchTerm, searchCountEl, searchResultsGrid);
        } else {
          // Fallback to Predictive Search
          searchWithPredictiveApi(searchTerm, searchCountEl, searchResultsGrid);
        }
      });
      return;
    }

    // No Storefront API available, use Predictive Search API fallback
    searchWithPredictiveApi(searchTerm, searchCountEl, searchResultsGrid);
  }

  /**
   * Fallback search using Shopify Predictive Search API
   * Limited to 10 results by Shopify - this is a platform limitation
   */
  function searchWithPredictiveApi(searchTerm, searchCountEl, searchResultsGrid) {
    // Use Shopify Predictive Search API
    // Prefix search with "wholesale" to prioritize wholesale products
    var searchQuery = 'wholesale ' + searchTerm;
    var url = '/search/suggest.json?q=' + encodeURIComponent(searchQuery) +
              '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last';

    fetch(url)
      .then(function(response) {
        if (!response.ok) throw new Error('Search failed');
        return response.json();
      })
      .then(function(data) {
        var products = [];
        if (data.resources && data.resources.results && data.resources.results.products) {
          products = data.resources.results.products;
        }

        // Filter to only wholesale products (must have "Wholesale" in title)
        var filteredProducts = products.filter(function(product) {
          return product.title && product.title.toLowerCase().includes('wholesale');
        });

        if (filteredProducts.length === 0) {
          renderSearchResults([], searchTerm, searchCountEl, searchResultsGrid);
          return;
        }

        // Fetch full product details for each result to get variant IDs and accurate pricing
        var productFetches = filteredProducts.map(function(product) {
          return fetch('/products/' + product.handle + '.js')
            .then(function(res) { return res.json(); })
            .catch(function() { return null; });
        });

        return Promise.all(productFetches).then(function(fullProducts) {
          // Filter out any failed fetches
          var validProducts = fullProducts.filter(function(p) { return p !== null; });
          renderSearchResults(validProducts, searchTerm, searchCountEl, searchResultsGrid);
        });
      })
      .catch(function(error) {
        console.error('Search error:', error);
        searchResultsGrid.innerHTML = '<li class="wholesale-grid__no-results">Search failed. Please try again.</li>';
      });
  }

  /**
   * Render search results as wholesale cards
   */
  function renderSearchResults(products, searchTerm, countEl, gridEl) {
    if (countEl) {
      countEl.textContent = products.length + ' result' + (products.length !== 1 ? 's' : '') + ' for "' + searchTerm + '"';
    }

    if (products.length === 0) {
      gridEl.innerHTML = '<li class="wholesale-grid__no-results">No products found matching "' + searchTerm + '"</li>';
      return;
    }

    // Build product cards HTML
    var html = '';
    products.forEach(function(product) {
      html += buildProductCardHTML(product);
    });

    gridEl.innerHTML = html;

    // Apply current view mode to search results
    var savedView = localStorage.getItem('wholesale-view') || 'grid';
    gridEl.classList.remove('view-grid', 'view-list');
    gridEl.classList.add('view-' + savedView);

    // Re-init quantity controls for new cards
    updateQuickOrderTotal();

    // Trigger VAT toggle refresh for new cards
    if (window.WholesaleVatToggle && typeof window.WholesaleVatToggle.refresh === 'function') {
      window.WholesaleVatToggle.refresh();
    }
    // Also dispatch event for any other listeners
    document.dispatchEvent(new CustomEvent('wholesale:products-loaded'));
  }

  /**
   * Build HTML for a single product card from search result
   * Matches wholesale card styling from card-product-wholesale.liquid
   * Uses metafield data for accurate wholesale pricing
   */
  function buildProductCardHTML(product) {
    // Get first available variant
    var variant = product.variants && product.variants[0] ? product.variants[0] : null;
    var variantId = variant ? variant.id : '';
    var sku = variant ? (variant.sku || '') : '';
    var priceInCents = variant ? variant.price : (product.price || 0);
    var available = product.available !== false;
    var isTaxable = variant ? variant.taxable : true;

    // Get product image
    var image = '';
    if (product.featured_image) {
      image = product.featured_image;
    } else if (product.images && product.images.length > 0) {
      image = product.images[0];
    }

    // Use metafield data for pack info (from Storefront API)
    // Falls back to title parsing for Predictive Search API results
    var packType = product.is_cdu ? 'CDU' : 'Case';
    var packQty = product.pack_quantity || 12;

    // Fallback: try to extract from title if no metafield data
    if (!product.pack_quantity) {
      var titleMatch = product.title.match(/\((Case|CDU|Cdu|CASE)\s*of\s*(\d+)\)/i);
      if (titleMatch) {
        // Normalize to "Case" or "CDU"
        packType = titleMatch[1].toUpperCase() === 'CDU' ? 'CDU' : 'Case';
        packQty = parseInt(titleMatch[2], 10);
      }
    }

    // Calculate unit price - divide total price by pack quantity
    var unitPriceInCents = Math.ceil(priceInCents / packQty);

    // Build pack badge text
    var packBadgeText = packType + ' of ' + packQty;

    // Build stock warning - only show for low stock or out of stock
    var stockLabel = '';
    var stockClass = '';
    var showStockWarning = false;
    if (variant) {
      var inventoryQty = variant.inventory_quantity || 0;
      if (inventoryQty <= 0 && !available) {
        stockLabel = 'Out of stock';
        stockClass = 'wholesale-card__stock--out';
        showStockWarning = true;
      } else if (inventoryQty > 0 && inventoryQty <= 50) {
        stockLabel = 'Low Stock';
        stockClass = 'wholesale-card__stock--low';
        showStockWarning = true;
      }
    }

    // Build case price HTML
    var casePriceHtml = '<div class="wholesale-card__case-price">' + packType + ' Price: ' + formatMoney(priceInCents) + '</div>';

    // Build RRP HTML if available (convert from decimal to pence)
    var rrpHtml = '';
    if (product.single_rrp) {
      var rrpInCents = Math.round(product.single_rrp * 100);
      rrpHtml = '<div class="wholesale-card__rrp">RRP:&nbsp;' + formatMoney(rrpInCents) + ' per unit</div>';
    }

    var productUrl = '/products/' + product.handle;

    return '<li class="wholesale-grid__item">' +
      '<div class="wholesale-card" data-product-id="' + product.id + '" data-variant-id="' + variantId + '" data-product-title="' + escapeHtml(product.title) + '" data-product-code="' + escapeHtml(sku) + '" data-product-sku="' + escapeHtml(sku) + '">' +
        '<div class="wholesale-card__media">' +
          '<a href="' + productUrl + '" class="wholesale-card__media-link">' +
            (image ? '<img src="' + image + '" alt="' + escapeHtml(product.title) + '" class="wholesale-card__image" loading="lazy">' : '<div class="wholesale-card__placeholder"></div>') +
          '</a>' +
          '<span class="wholesale-card__pack-badge">' + packBadgeText + '</span>' +
        '</div>' +
        '<div class="wholesale-card__info">' +
          '<h3 class="wholesale-card__title"><a href="' + productUrl + '">' + escapeHtml(product.title) + '</a></h3>' +
          (sku ? '<p class="wholesale-card__product-code">Code: ' + escapeHtml(sku) + '</p>' : '') +
          '<div class="wholesale-card__pricing">' +
            casePriceHtml +
            '<span class="wholesale-card__price-value" data-wholesale-price="' + unitPriceInCents + '" data-taxable="' + isTaxable + '">' + formatMoney(unitPriceInCents) + '/unit</span>' +
            '<span class="wholesale-card__price-vat" data-vat-label>Inc. VAT</span>' +
            rrpHtml +
          '</div>' +
          (showStockWarning ? '<p class="wholesale-card__stock ' + stockClass + '">' + stockLabel + '</p>' : '') +
        '</div>' +
        '<div class="wholesale-card__actions">' +
          '<form method="post" action="/cart/add" class="wholesale-card__form" data-type="add-to-cart-form">' +
            '<input type="hidden" name="id" value="' + variantId + '">' +
            '<div class="wholesale-card__quantity">' +
              '<button type="button" class="wholesale-card__qty-btn wholesale-card__qty-btn--minus" aria-label="Decrease quantity">-</button>' +
              '<input type="number" name="quantity" value="1" min="1" step="1" class="wholesale-card__qty-input" data-min-qty="1" data-price-per-unit="' + unitPriceInCents + '" aria-label="Quantity">' +
              '<button type="button" class="wholesale-card__qty-btn wholesale-card__qty-btn--plus" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="submit" class="wholesale-card__add-btn"' + (available ? '' : ' disabled') + '>' +
              (available ? 'Add to Cart' : 'Sold Out') +
              '<span class="wholesale-card__add-spinner" aria-hidden="true"><svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></span>' +
            '</button>' +
          '</form>' +
        '</div>' +
      '</div>' +
    '</li>';
  }

  /**
   * Clear search and restore original products
   */
  function clearSearch() {
    var searchResultsContainer = document.getElementById('wholesale-search-results');
    var productGrid = document.getElementById('product-grid');
    var pagination = document.querySelector('.pagination');

    isSearchActive = false;

    if (searchResultsContainer) {
      searchResultsContainer.style.display = 'none';
    }

    if (productGrid) {
      productGrid.style.display = '';
      // Show all cards again
      document.querySelectorAll('#product-grid .wholesale-card').forEach(function(card) {
        card.style.display = '';
      });
    }

    if (pagination) {
      pagination.style.display = '';
    }

    // Remove any no results message
    var noResults = document.getElementById('wholesale-no-results');
    if (noResults) {
      noResults.style.display = 'none';
    }

    updateQuickOrderTotal();
  }

  /**
   * Escape HTML special characters
   */
  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  /**
   * Load all products in the collection via Storefront API
   * Called on page load if token is available
   * Products are cached for instant search filtering
   */
  function loadAllCollectionProducts() {
    if (isLoadingProducts || cachedCollectionProducts) return productsLoadPromise;

    isLoadingProducts = true;

    productsLoadPromise = fetchAllCollectionProducts()
      .then(function(products) {
        cachedCollectionProducts = products;
        isLoadingProducts = false;
        console.log('Wholesale search: Loaded ' + products.length + ' products from collection');
        return products;
      })
      .catch(function(error) {
        console.error('Failed to load collection products:', error);
        isLoadingProducts = false;
        cachedCollectionProducts = null;
        return null;
      });

    return productsLoadPromise;
  }

  /**
   * Fetch all products from a collection using Storefront API GraphQL
   * Handles pagination to get all products (not just first 250)
   */
  function fetchAllCollectionProducts() {
    var allProducts = [];
    var hasNextPage = true;
    var cursor = null;

    function fetchPage() {
      var query = buildCollectionProductsQuery(currentCollectionHandle, cursor);

      return fetch('https://' + shopDomain + '/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontApiToken
        },
        body: JSON.stringify({ query: query })
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Storefront API request failed');
        return response.json();
      })
      .then(function(data) {
        if (data.errors) {
          throw new Error(data.errors[0].message);
        }

        var collection = data.data.collection;
        if (!collection) {
          throw new Error('Collection not found');
        }

        var products = collection.products;
        var edges = products.edges || [];

        // Transform GraphQL products to our format
        edges.forEach(function(edge) {
          var product = transformGraphQLProduct(edge.node);
          if (product) {
            allProducts.push(product);
          }
        });

        // Check for more pages
        hasNextPage = products.pageInfo.hasNextPage;
        if (hasNextPage && edges.length > 0) {
          cursor = edges[edges.length - 1].cursor;
          return fetchPage();
        }

        return allProducts;
      });
    }

    return fetchPage();
  }

  /**
   * Build GraphQL query for collection products
   * Includes metafields for wholesale pricing: min_quantity, cdu, single_rrp
   */
  function buildCollectionProductsQuery(handle, cursor) {
    var afterClause = cursor ? ', after: "' + cursor + '"' : '';

    return '{\
      collection(handle: "' + handle + '") {\
        products(first: 250' + afterClause + ') {\
          pageInfo {\
            hasNextPage\
          }\
          edges {\
            cursor\
            node {\
              id\
              title\
              handle\
              availableForSale\
              featuredImage {\
                url\
              }\
              minQuantity: metafield(namespace: "custom", key: "wholesale_min_quantity") {\
                value\
              }\
              isCdu: metafield(namespace: "custom", key: "wholesale_cdu") {\
                value\
              }\
              singleRrp: metafield(namespace: "custom", key: "wholesale_single_rrp") {\
                value\
              }\
              variants(first: 1) {\
                edges {\
                  node {\
                    id\
                    sku\
                    price {\
                      amount\
                    }\
                    quantityAvailable\
                    taxable\
                  }\
                }\
              }\
            }\
          }\
        }\
      }\
    }';
  }

  /**
   * Transform GraphQL product to format expected by buildProductCardHTML
   * Includes metafield data for wholesale pricing
   */
  function transformGraphQLProduct(graphqlProduct) {
    var variant = null;
    if (graphqlProduct.variants && graphqlProduct.variants.edges && graphqlProduct.variants.edges.length > 0) {
      var variantNode = graphqlProduct.variants.edges[0].node;
      variant = {
        id: variantNode.id.replace('gid://shopify/ProductVariant/', ''),
        sku: variantNode.sku || '',
        price: Math.round(parseFloat(variantNode.price.amount) * 100),
        inventory_quantity: variantNode.quantityAvailable || 0,
        taxable: variantNode.taxable !== false
      };
    }

    // Extract metafield values
    var packQuantity = 12; // default
    if (graphqlProduct.minQuantity && graphqlProduct.minQuantity.value) {
      packQuantity = parseInt(graphqlProduct.minQuantity.value, 10) || 12;
    }

    var isCdu = false;
    if (graphqlProduct.isCdu && graphqlProduct.isCdu.value) {
      isCdu = graphqlProduct.isCdu.value === 'true';
    }

    var singleRrp = null;
    if (graphqlProduct.singleRrp && graphqlProduct.singleRrp.value) {
      singleRrp = parseFloat(graphqlProduct.singleRrp.value);
    }

    return {
      id: graphqlProduct.id.replace('gid://shopify/Product/', ''),
      title: graphqlProduct.title,
      handle: graphqlProduct.handle,
      available: graphqlProduct.availableForSale,
      featured_image: graphqlProduct.featuredImage ? graphqlProduct.featuredImage.url : null,
      variants: variant ? [variant] : [],
      price: variant ? variant.price : 0,
      // Wholesale metafields
      pack_quantity: packQuantity,
      is_cdu: isCdu,
      single_rrp: singleRrp
    };
  }

  /**
   * Search cached collection products
   * Returns products matching the search term
   */
  function searchCachedProducts(searchTerm) {
    if (!cachedCollectionProducts) return [];

    var term = searchTerm.toLowerCase();

    return cachedCollectionProducts.filter(function(product) {
      // Search in title
      if (product.title && product.title.toLowerCase().indexOf(term) !== -1) {
        return true;
      }

      // Search in SKU
      if (product.variants && product.variants.length > 0) {
        var sku = product.variants[0].sku || '';
        if (sku.toLowerCase().indexOf(term) !== -1) {
          return true;
        }
      }

      // Search in handle (URL slug)
      if (product.handle && product.handle.toLowerCase().indexOf(term) !== -1) {
        return true;
      }

      return false;
    });
  }

  /**
   * Filter products on current page (fallback/local filter)
   */
  function filterProductsLocal(searchTerm) {
    var cards = document.querySelectorAll('#product-grid .wholesale-card');
    var visibleCount = 0;

    cards.forEach(function(card) {
      if (!searchTerm) {
        card.style.display = '';
        visibleCount++;
        return;
      }

      var title = (card.dataset.productTitle || '').toLowerCase();
      var code = (card.dataset.productCode || '').toLowerCase();
      var sku = (card.dataset.productSku || '').toLowerCase();

      var matches = title.includes(searchTerm) ||
                    code.includes(searchTerm) ||
                    sku.includes(searchTerm);

      if (matches) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    updateQuickOrderTotal();
  }

  /**
   * Collection Description Toggle
   * Expands/collapses the collection description below the grid
   */
  function initDescriptionToggle() {
    var toggleBtn = document.querySelector('.wholesale-grid__description-toggle');
    var descriptionDiv = document.getElementById('wholesale-description');

    if (!toggleBtn || !descriptionDiv) return;

    toggleBtn.addEventListener('click', function() {
      var isExpanded = descriptionDiv.classList.contains('expanded');
      var toggleText = toggleBtn.querySelector('.wholesale-grid__description-toggle-text');

      if (isExpanded) {
        descriptionDiv.classList.remove('expanded');
        toggleBtn.classList.remove('expanded');
        toggleBtn.setAttribute('aria-expanded', 'false');
        if (toggleText) toggleText.textContent = 'View more';
      } else {
        descriptionDiv.classList.add('expanded');
        toggleBtn.classList.add('expanded');
        toggleBtn.setAttribute('aria-expanded', 'true');
        if (toggleText) toggleText.textContent = 'View less';
      }
    });
  }

  /**
   * Bulk Add Button in Toolbar
   * Adds all visible items on screen to cart
   */
  function initBulkAddButton() {
    var bulkAddBtn = document.getElementById('wholesale-bulk-add');
    if (!bulkAddBtn) return;

    bulkAddBtn.addEventListener('click', function(e) {
      e.preventDefault();
      handleToolbarBulkAdd(bulkAddBtn);
    });
  }

  function handleToolbarBulkAdd(button) {
    // Collect all visible items with quantity > 0
    var items = [];

    document.querySelectorAll('.wholesale-card').forEach(function(card) {
      // Skip hidden cards (filtered out by search)
      if (card.style.display === 'none') return;

      var input = card.querySelector('.wholesale-card__qty-input');
      var variantId = card.dataset.variantId;
      var qty = parseInt(input.value, 10) || 0;

      // Include all items with qty >= 1
      if (qty >= 1 && variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: qty
        });
      }
    });

    if (items.length === 0) {
      // No items to add - show message briefly
      var btnText = button.querySelector('.btn-text');
      var originalText = btnText ? btnText.textContent : 'Bulk Add All';
      if (btnText) btnText.textContent = 'No items selected';
      setTimeout(function() {
        if (btnText) btnText.textContent = originalText;
      }, 2000);
      return;
    }

    // Show loading state
    button.classList.add('loading');
    button.disabled = true;

    // Try Liquid Ajax Cart first, then fallback to fetch
    if (window.liquidAjaxCart && typeof window.liquidAjaxCart.cartRequestAdd === 'function') {
      window.liquidAjaxCart.cartRequestAdd({ items: items }, {
        lastComplete: function(requestState) {
          if (requestState.responseData && requestState.responseData.ok) {
            handleToolbarBulkAddSuccess(button, items.length);
          } else {
            handleToolbarBulkAddError(button, requestState.fetchError || 'Add to cart failed');
          }
        }
      });
    } else {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: items })
      })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      })
      .then(function() {
        handleToolbarBulkAddSuccess(button, items.length);

        // Refresh cart state
        if (window.liquidAjaxCart && window.liquidAjaxCart.cartRequestGet) {
          window.liquidAjaxCart.cartRequestGet();
        }
      })
      .catch(function(error) {
        handleToolbarBulkAddError(button, error);
      });
    }
  }

  function handleToolbarBulkAddSuccess(button, itemCount) {
    button.classList.remove('loading');
    button.disabled = false;

    var btnText = button.querySelector('.btn-text');
    if (btnText) {
      btnText.textContent = itemCount + ' Added!';
      setTimeout(function() {
        btnText.textContent = 'Bulk Add All';
      }, 2000);
    }

    // Update quick order display if present
    updateQuickOrderTotal();
  }

  function handleToolbarBulkAddError(button, error) {
    console.error('Bulk add error:', error);
    button.classList.remove('loading');
    button.disabled = false;

    var btnText = button.querySelector('.btn-text');
    if (btnText) {
      btnText.textContent = 'Error - Try Again';
      setTimeout(function() {
        btnText.textContent = 'Bulk Add All';
      }, 3000);
    }
  }

  /**
   * Facets.js Integration
   * Re-initializes after AJAX filter updates
   */
  function hookIntoFacets() {
    // Check if FacetFiltersForm exists
    if (typeof FacetFiltersForm === 'undefined') {
      // Try again after a short delay
      setTimeout(hookIntoFacets, 100);
      return;
    }

    // Store original method
    var originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;

    // Override to add our re-initialization
    FacetFiltersForm.renderProductGridContainer = function(html) {
      // Inject saved view class into the HTML before it hits the DOM
      var savedView = localStorage.getItem('wholesale-view') || 'grid';
      if (savedView === 'list') {
        // Replace all occurrences (main grid + search results grid)
        while (html.indexOf('wholesale-grid__products view-grid') !== -1) {
          html = html.replace('wholesale-grid__products view-grid', 'wholesale-grid__products view-list');
        }
      }

      // Call original method with patched HTML
      originalRenderProductGridContainer.call(this, html);

      // Sync DOM immediately as a safety net
      var productGrid = document.getElementById('product-grid');
      if (productGrid) {
        productGrid.classList.remove('view-grid', 'view-list');
        productGrid.classList.add('view-' + savedView);
      }
      var searchGrid = document.getElementById('search-results-grid');
      if (searchGrid) {
        searchGrid.classList.remove('view-grid', 'view-list');
        searchGrid.classList.add('view-' + savedView);
      }

      // Re-initialize controls on new DOM elements
      setTimeout(function() {
        initQuantityControls();
        initFormSubmitHandlers();
        updateQuickOrderTotal();
      }, 50);
    };
  }

  // Hook into facets after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hookIntoFacets);
  } else {
    hookIntoFacets();
  }

  // Expose updateQuickOrderTotal for external use if needed
  window.wholesaleCollection = {
    updateQuickOrderTotal: updateQuickOrderTotal
  };

})();
